import React, { FC, useContext, useEffect, useState } from "react";
import classnames from "classnames";

import { AppContext } from "@vahak/core/dist/app-context";
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { InputChangeEventHandler } from "@vahak/core/dist/custom-types/handler";
import { usePlaceSearch, useStateSearch } from "@vahak/core/dist/hooks/useSearch";
import { PopularDest } from "../constants";
import { useTopDest } from "@vahak/core/dist/hooks/useTopDest";
import MobileSearchCard from "../mobile-search-card/MobileSearchCard";
import Cover from "../Cover";
import EditSearchModal, { EditSearchModalProps } from "../edit-search-modal/EditSearchModal";
import { getHeightByViewPort } from "@vahak/core/dist/methods/getSizeByViewPort";
import SearchFullScreenMobile from "@vahak/core/dist/components/search-full-screen-mobile/SearchFullScreenMobile";
import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";

interface EditSearchVals {
    searchBy?: "location" | "name";
    src?: CitySearchResultWithCateg;
    dest?: CitySearchResultWithCateg;
    name: string;
}
const formattedPopularDest = PopularDest.map((state) => ({
    ...state,
    category: "suggested" as "suggested",
    categoryLabel: "POPULAR DESTINATIONS" as "POPULAR DESTINATIONS"
}));
export interface MobileProps {
    isSearchCardMinimized: boolean;
    className?: string;
    topContainerClassName?: string;
    searchCardClassName?: string;
    searchBy?: EditSearchModalProps["searchBy"];
    onChangeSearchBy: EditSearchModalProps["onChangeSearchBy"];
    src: CitySearchResultWithCateg | undefined;
    onSelectSrc: (city?: CitySearchResultWithCateg) => void;
    onClearSrc: () => void;
    dest: CitySearchResultWithCateg | undefined;
    onSelectDest: (city: CitySearchResultWithCateg | undefined) => void;
    selectedDestQuickFilter: CitySearchResultWithCateg;
    nameSearchVal: string;
    onInputNameSearch: InputChangeEventHandler;
    onClearName?: () => void;
    onClickUpdateSearch: (editVals: EditSearchVals) => void;
    isSrcSearchVisible?: boolean | undefined;
    setisSrcSearchVisible: (val: any) => void;
    isDestSearchVisible?: boolean | undefined;
    setisDestSearchVisible: (val: any) => void;
    isEditSearchModalVisible?: boolean | undefined;
    setisEditSearchModalVisible: (val: any) => void;
    srcParam?: string;
}

const Mobile: FC<MobileProps> = ({
    isSearchCardMinimized,
    className,
    topContainerClassName,
    searchCardClassName,
    searchBy,
    src,
    onSelectSrc,
    onClearSrc,
    dest,
    onSelectDest,
    selectedDestQuickFilter,
    nameSearchVal,
    onClearName,
    onClickUpdateSearch,
    isSrcSearchVisible,
    setisSrcSearchVisible,
    isDestSearchVisible,
    setisDestSearchVisible,
    isEditSearchModalVisible,
    setisEditSearchModalVisible,
    srcParam
}) => {
    const { currentLocation, recentTransporterCitySearches } = useContext(AppContext);

    const { topDests, setTopDests } = useTopDest({ max: 2 });

    const [editSearchVals, updateEditSearchVals] = useState<EditSearchVals>({ name: "" });

    const onChangeEditSearchBy: EditSearchModalProps["onChangeSearchBy"] = (e) => {
        e.persist();
        const val = e.currentTarget.value;
        updateEditSearchVals((state) => ({ ...state, searchBy: val }));
    };

    const onChangeEditSrc = (selected?: CitySearchResultWithCateg) => {
        updateEditSearchVals((state) => ({ ...state, src: selected }));
        setisSrcSearchVisible(false);
    };

    const onChangeEditDest = (selected: CitySearchResultWithCateg | undefined) => {
        updateEditSearchVals((state) => ({ ...state, dest: selected }));
        if (isDestSearchVisible) {
            setisDestSearchVisible(false);
        }
    };

    const onChangeEditName: EditSearchModalProps["onInputNameSearch"] = (e) => {
        e.persist();
        const name = e.currentTarget.value;
        updateEditSearchVals((state) => ({ ...state, name }));
    };

    const onClearEditSrc = () => {
        updateEditSearchVals((state) => ({ ...state, src: undefined }));
    };

    const onClearEditName = () => {
        updateEditSearchVals((state) => ({ ...state, name: "" }));
    };

    const {
        isSearchLoading: isSourceSearchLoading,
        setIsSearchLoading: setIsSourceSearchLoading,
        onSearchInput: onSourceSearchInput,
        searchOptions: sourceSearchOptions,
        searchText: sourceSearchText,
        labelKey: sourceSearchLabelKey,
        onInputChange: onSourceSearchInputChange,
        onItemSelected: onSourceSearchItemSelected,
        selectedItem: selectedSrcInSearch,
        setSelectedItem: setSelectedSrcInSearch
    } = usePlaceSearch({
        locationParamsOption: { source: LocationSearchSource.TransporterProfile },
        showCurrentLocationOption: true,
        defaultOptions: recentTransporterCitySearches,
        onSelectItem: (selectedItems: CitySearchResultWithCateg[]) => {
            isEditSearchModalVisible ? onChangeEditSrc(selectedItems?.[0]) : onSelectSrc(selectedItems?.[0]);
        }
    });

    const {
        labelKey: destSearchLabelKey,
        isSearchLoading: isDestSearchLoading,
        onSearchInput: onDestSearchInput,
        searchOptions: destSearchOptions,
        searchText: destSearchText,
        onInputChange: onDestSearchInputChange,
        onItemSelected: onDestSearchItemSelected,
        selectedItem: selectedDestInSearch,
        setSelectedItem: setSelectedDestInSearch
    } = useStateSearch({
        locationParamsOption: { source: LocationSearchSource.TransporterProfile },
        showCurrentLocationOption: true,
        defaultOptions: formattedPopularDest,
        onSelectItem: (item: CitySearchResultWithCateg[]) => {
            setTopDests(item[0] as any);
            isEditSearchModalVisible ? onChangeEditDest(item[0]) : onSelectDest(item[0]);
        }
    });

    const onClickSearchSource = () => {
        setisSrcSearchVisible(true);
        setSelectedSrcInSearch(undefined);
    };

    const onClickDestSearchBtn = () => {
        setisDestSearchVisible(true);
        setSelectedDestInSearch(undefined);
    };

    const onClickSourceSearchBackBtn = () => {
        setisSrcSearchVisible(false);
    };

    const onClickDestSearchBackBtn = () => {
        setisDestSearchVisible(false);
    };

    const onClickDestQuickFilter = (state: CitySearchResultWithCateg) => {
        isEditSearchModalVisible ? onChangeEditDest(state) : onSelectDest(state);
        setSelectedDestInSearch(state);
        setTopDests(state);
    };

    const onDeselectDestQuickFilter = () => {
        isEditSearchModalVisible ? onChangeEditDest(undefined) : onSelectDest(undefined);
        setSelectedDestInSearch(undefined);
    };

    const onClickEditSearch = () => {
        setisEditSearchModalVisible(true);
    };

    const onToggleEditSearchModal = () => {
        setisEditSearchModalVisible(!isEditSearchModalVisible);
    };

    const _onClickUpdateSearch = () => {
        window.scrollTo({ left: 0, top: +getHeightByViewPort(74).slice(0, -2), behavior: "auto" });

        onClickUpdateSearch(editSearchVals);
    };

    useEffect(() => {
        if (currentLocation?.lat) {
            setSelectedSrcInSearch({ name: String(currentLocation?.lat) });
        }
        setIsSourceSearchLoading(false);
    }, [currentLocation]);

    useEffect(() => {
        selectedDestQuickFilter.state_id && setTopDests(selectedDestQuickFilter);
    }, [selectedDestQuickFilter]);

    useEffect(() => {
        isEditSearchModalVisible && updateEditSearchVals({ searchBy, src, dest, name: nameSearchVal });
    }, [isEditSearchModalVisible]);

    return (
        <div className={className}>
            <div className={classnames(topContainerClassName)}>
                <Cover
                    isMinimized={isSearchCardMinimized}
                    style={
                        {
                            // height: isSearchCardMinimized
                            //     ? "56px"
                            //     : searchBy === "name"
                            //     ? "117px"
                            //     : "calc(2*20px + 2*47px + 90px )",
                            // transition: "height 0.4s"
                        }
                    }
                    cityName={srcParam}
                />
                <div className={classnames("container", searchCardClassName)}>
                    <MobileSearchCard
                        isCardMinimized={isSearchCardMinimized}
                        searchBy={searchBy}
                        nameSearchVal={nameSearchVal}
                        sourceSearchLabelKey={sourceSearchLabelKey}
                        selectedSource={src}
                        onClearSrc={onClearSrc}
                        onClickSearchSource={onClickSearchSource}
                        topDests={topDests}
                        selectedDest={dest}
                        onClickDestQuickFilter={onClickDestQuickFilter}
                        onDeselectDestSearchBtn={onDeselectDestQuickFilter}
                        onClickDestSearchBtn={onClickDestSearchBtn}
                        onClickEdit={onClickEditSearch}
                        onClearName={onClearName}
                    />
                </div>
            </div>

            <SearchFullScreenMobile
                isVisible={!!isSrcSearchVisible}
                onClickBackBtn={onClickSourceSearchBackBtn}
                id="transporter-source-search"
                isLoading={isSourceSearchLoading}
                onSearch={onSourceSearchInput}
                onSelectItem={onSourceSearchItemSelected}
                onInputChange={onSourceSearchInputChange}
                selectedItem={selectedSrcInSearch}
                options={sourceSearchOptions}
                searchText={sourceSearchText}
                labelText=""
                labelKey={sourceSearchLabelKey}
                placeholder="Where is your Loading Point?"
            />

            <SearchFullScreenMobile
                isVisible={!!isDestSearchVisible}
                onClickBackBtn={onClickDestSearchBackBtn}
                id="transporter-dest-search"
                isLoading={isDestSearchLoading}
                onSearch={onDestSearchInput}
                onSelectItem={onDestSearchItemSelected}
                onInputChange={onDestSearchInputChange}
                selectedItem={selectedDestInSearch}
                options={destSearchOptions}
                searchText={destSearchText}
                labelText=""
                labelKey={destSearchLabelKey}
                placeholder="Search Destination State"
            />
            <EditSearchModal
                isVisible={!!isEditSearchModalVisible}
                handleToggle={onToggleEditSearchModal}
                nameSearchVal={editSearchVals.name}
                onInputNameSearch={onChangeEditName}
                onClearName={onClearEditName}
                sourceSearchLabelKey={sourceSearchLabelKey}
                selectedSource={editSearchVals.src}
                onClearSrc={onClearEditSrc}
                onClickSearchSource={onClickSearchSource}
                topDests={topDests}
                selectedDest={editSearchVals.dest}
                onClickDestQuickFilter={onClickDestQuickFilter}
                onClickDestSearchBtn={onClickDestSearchBtn}
                onDeselectDestSearchBtn={onDeselectDestQuickFilter}
                onClickUpdateSearch={_onClickUpdateSearch}
                searchBy={editSearchVals.searchBy}
                onChangeSearchBy={onChangeEditSearchBy}
            />
        </div>
    );
};

export default Mobile;
